import { FC, useState } from 'react';

import { CalendarOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { Col, Row, Select, Tag } from 'antd';
import dayjs from 'dayjs';
import { Button, Label, Typography } from 'ui';

import { EventScheduleReqType } from 'types/promoters/promoterTypes';

import s from './EventSchedule.module.scss';
import DayItem from './dayItem';

interface ScheduleRowProps {
  schedule: EventScheduleReqType;
  onUpdate: (updatedSchedule: EventScheduleReqType) => void;
  isLoading: boolean;
  deleting: boolean;
  actingId?: string;
  onEdit: () => void;
  onDelete: (id?: string) => void;
  onSave: (id?: string) => void;
}

const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const ScheduleRow: FC<ScheduleRowProps> = ({
  schedule,
  isLoading,
  deleting,
  onUpdate,
  actingId,
  onSave,
  onEdit,
  onDelete,
}) => {
  const [showDayForm, setShowDayForm] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | undefined>();
  const [editingDayIndex, setEditingDayIndex] = useState<number | null>(null);
  const [dayError, setDayError] = useState<boolean>(false);

  const hasAtLeastOneDayAndTime = (schedule: EventScheduleReqType): boolean => {
    return schedule.days.some((day) => day.timeslots.length > 0);
  };

  return (
    <div className={s.scheduleRowWrapper}>
      <div className={s.desktopRow}>
        <Typography type="main">{schedule.name}</Typography>
        <Typography type="main">{dayjs(schedule.dateFrom).format('YYYY-MM-DD')}</Typography>
        <Typography type="main">{dayjs(schedule.dateTo).format('YYYY-MM-DD')}</Typography>
        <div>
          <Tag color={schedule.active ? 'cyan' : 'error'}>{schedule.active ? 'True' : 'False'}</Tag>
        </div>
        <div className={s.actionButtonContainter}>
          <Button className={s.actionButton} onClick={() => setShowDayForm((prev) => !prev)}>
            <CalendarOutlined />
            DAYS
          </Button>
          <Button className={s.actionButton} onClick={onEdit}>
            <EditOutlined />
            EDIT
          </Button>
          <Button
            loading={deleting && actingId === schedule?.id}
            className={s.deleteButton}
            onClick={() => onDelete(schedule?.id)}>
            <DeleteOutlined />
            DELETE
          </Button>
          <Button
            disabled={!hasAtLeastOneDayAndTime(schedule)}
            loading={isLoading && actingId === schedule?.id}
            type="primary"
            onClick={() => onSave(schedule?.id)}>
            <SaveOutlined />
            SAVE
          </Button>
        </div>
      </div>

      <div className={s.mobileRow}>
        <Row gutter={[8, 0]}>
          <Col span={12}>
            <Label>FRIENDLY NAME</Label>
            <Typography type="main">{schedule.name}</Typography>
          </Col>
          <Col span={12}>
            <Label>FROM</Label>
            <Typography type="main">{dayjs(schedule.dateFrom).format('YYYY-MM-DD')}</Typography>
          </Col>
        </Row>
        <Row gutter={[8, 0]} style={{ marginTop: 24, marginBottom: 24 }}>
          <Col span={12}>
            <Label>TO</Label>
            <Typography type="main">{dayjs(schedule.dateTo).format('YYYY-MM-DD')}</Typography>
          </Col>
          <Col span={12}>
            <Label>ACTIVE?</Label>
            <Tag color={schedule.active ? 'cyan' : 'error'}>
              {schedule.active ? 'True' : 'False'}
            </Tag>
          </Col>
        </Row>
        <Row gutter={[8, 20]}>
          <Col span={24}>
            <Label>ACTION</Label>
            <div className={s.actionButtonContainter}>
              <Button className={s.actionButton} onClick={() => setShowDayForm((prev) => !prev)}>
                <CalendarOutlined />
                DAYS
              </Button>
              <Button className={s.actionButton} onClick={onEdit}>
                <EditOutlined />
                EDIT
              </Button>
              <Button
                loading={deleting && actingId === schedule?.id}
                className={s.deleteButton}
                onClick={() => onDelete(schedule?.id)}>
                <DeleteOutlined />
                DELETE
              </Button>
              <Button
                className={s.saveButton}
                disabled={!hasAtLeastOneDayAndTime(schedule)}
                loading={isLoading && actingId === schedule?.id}
                type="primary"
                onClick={() => onSave(schedule?.id)}>
                <SaveOutlined />
                SAVE
              </Button>
            </div>
          </Col>
        </Row>
      </div>
      {showDayForm && (
        <div className={s.dayForm}>
          <Typography type="h6" weight={800}>
            Day of week
          </Typography>
          <div>
            {schedule.days.map((day, idx) => (
              <DayItem
                key={idx}
                day={day}
                onUpdate={(updatedDay) => {
                  const updatedDays = schedule.days.map((d) =>
                    d.dayOfWeek === updatedDay.dayOfWeek ? updatedDay : d,
                  );
                  onUpdate({ ...schedule, days: updatedDays });
                }}
                onDelete={() => {
                  const filteredDays = schedule.days.filter((d) => d.dayOfWeek !== day.dayOfWeek);
                  onUpdate({ ...schedule, days: filteredDays });
                }}
                onEdit={() => {
                  setSelectedDay(day.dayOfWeek);
                  setEditingDayIndex(idx);
                }}
              />
            ))}
          </div>
          <Label>Add a day of week for this schedule when the event will happen.</Label>
          <div className={s.addDayButtonWrapper}>
            <div className={s.addDaySelector}>
              <Select
                placeholder="Select a day"
                value={selectedDay}
                style={{ height: 41, width: '100%' }}
                onChange={(value) => {
                  setSelectedDay(value);
                  setDayError(false);
                }}>
                {allDays
                  .filter((day) => {
                    const used = schedule.days.map((d) => d.dayOfWeek);
                    const isEditing = editingDayIndex !== null;
                    const currentDayName =
                      isEditing && schedule.days[editingDayIndex]
                        ? schedule.days[editingDayIndex].dayOfWeek
                        : null;

                    return !used.includes(day) || (isEditing && day === currentDayName);
                  })
                  .map((day) => (
                    <Select.Option key={day} value={day}>
                      {day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
              </Select>
              {dayError && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  Please select a valid day
                </div>
              )}
            </div>
            {editingDayIndex === null ? (
              <Button
                className={s.addDaySelectorButton}
                type="primary"
                onClick={() => {
                  if (!selectedDay || schedule.days.some((d) => d.dayOfWeek === selectedDay)) {
                    setDayError(true);
                    return;
                  }

                  onUpdate({
                    ...schedule,
                    days: [...schedule.days, { dayOfWeek: selectedDay, timeslots: [] }],
                  });

                  setSelectedDay(undefined);
                  setDayError(false);
                }}>
                ADD DAY
              </Button>
            ) : (
              <Button
                type="primary"
                className={s.addDaySelectorButton}
                onClick={() => {
                  if (!selectedDay) {
                    setDayError(true);
                    return;
                  }

                  const updatedDays = [...schedule.days];
                  const original = updatedDays[editingDayIndex];

                  updatedDays[editingDayIndex] = {
                    ...original,
                    dayOfWeek: selectedDay, // update name only
                  };

                  onUpdate({ ...schedule, days: updatedDays });

                  // Reset editing state
                  setEditingDayIndex(null);
                  setSelectedDay(undefined);
                  setDayError(false);
                }}>
                UPDATE DAY
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleRow;
